import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import * as Highcharts from 'highcharts';
import WordCloud from "highcharts/modules/wordcloud.js";
import { constants } from 'src/app/shared/constants';
import { CVE } from 'src/app/shared/model/cve';
import {  VulnSearchCriteria, VulnSeverityStatsCriteria } from 'src/app/shared/model/searchCriteria';
import { ErrorService } from 'src/app/shared/services/error.service';
import { UserPreferenceService } from 'src/app/shared/services/userpreferences.service';
import { VulnerabilitiesService } from 'src/app/shared/services/vulnerabilities.service';
import { Utilities } from 'src/app/shared/utilities';
import { WordCloudItem } from 'src/app/shared/model/signal';
import { ActivatedRoute } from '@angular/router';

WordCloud(Highcharts);
@Component({
  selector: 'app-vulnerabilities',
  templateUrl: './vulnerabilities.component.html',
  styleUrls: ['./vulnerabilities.component.scss']
})
export class VulnerabilitiesComponent implements OnInit {
  // pagination
  perPage = constants.recordsPerPage.vulns;
  currentPage: number;

  // data
  searchCriteria: VulnSearchCriteria;
  searched: boolean;
  vulnsLoaded: boolean;
  vulns: CVE[];
  vulnsCount: number;

  // saved searches
  savedSearches: VulnSearchCriteria[];
  searchName: string;
  hideSaveSearch: boolean;

  // user prefs
  flags: any[];
  flagRefs: string[];
  flaggingRef: string //used to stop the flapping of flags whilst waiting for update to complete
  watchedProducts: string[];

  // layout
  advSearch: boolean;
  viewType: string;
  page: string;

  // search fields
  cve: string;
  vendor: string;
  product: string;
  description: string;
  severity: string;
  dateFrom: string;
  dateTo: string;
  cpe: string;
  sort: string;
  sortOrder: number;

  // stats
  sinceLastLogin: number;
  totalVendors: number;
  totalProducts: number;
  totalVulnerabilities: number;
  lastUpdated: string;

  // charts
  Highcharts: typeof Highcharts = Highcharts;
  analyticsDataLoaded: boolean;

  updateStackedBarSeverity: boolean;
  loadStackedBarSeverity = false;
  stackedBarSeverityOptions = {   
    chart: {
      type: 'column',
      backgroundColor: '#F3F3F3'
    },
    //colors: ['#1961ac', '#4bb4e6', '#bcdff7', '#007836', '#50be87', '#bbdecf'],
    colors: constants.graphColors,
    title: {
      align: 'right',
      text: '12 Month Trend Analysis - Severity'
    },
    legend : {
       layout: 'horizontal',
       align: 'left',
       verticalAlign: 'top',
       floating: true,
       borderWidth: 1
    },
    xAxis:{
      categories: ["None", "Low", "Medium", "High", "Critical"], 
      title: {
          text: null
       } 
    },
    yAxis : {
       min: 0, title: {
          text: 'Vulnerabilities', 
          align: 'high'
       },
       labels: {
          overflow: 'justify'
       }
    },
    tooltip : {
       valueSuffix: ' vulnerabilities published'
    },
    plotOptions : {
       bar: {
          dataLabels: {
             enabled: true
          }
       },
       series: {
          stacking: 'normal'
       }
    },
    credits:{
       enabled: false
    },
    series: [
    ]
  };

  updateUrgencyPie: boolean;
  urgPieChartOptions = {   
    //colors: ['#1961ac', '#4bb4e6', '#bcdff7', '#007836', '#50be87', '#bbdecf'],
    colors: constants.graphColors,
    chart : {
       plotBorderWidth: null,
       plotShadow: false,
       backgroundColor: '#F3F3F3'
    },
    title : {
       text: 'Severity'   
    },
    tooltip : {
       pointFormat: '<b>{point.percentage:.1f}%</b>'
    },
    plotOptions : {
       pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
             enabled: false           
          },
          showInLegend: true
       }
    },
    series : [{
       type: 'pie',
       data: [
       ]
    }],
    credits: {
      enabled: false
    }
  };
  
  updateWordChart: boolean;
  wordChartOptions = {   
    colors: constants.graphColors,
    chart : {
       type: 'wordcloud',
       backgroundColor: '#F3F3F3'
    },
    title : {
       text: 'Keywords'   
    },
    series: [{
      data: [],
      type: undefined
    }],
    credits: {
      enabled: false
    }
  };

  datepipe: DatePipe = new DatePipe('en-GB')
  
  // initial page to load
  loadpage: string;

  constructor(private vulnService: VulnerabilitiesService,
              private userprefService: UserPreferenceService, 
              private errorService: ErrorService,
              private modalService: NgbModal,
              private route: ActivatedRoute) { }

  ngOnInit() {
    this.route.params.subscribe(params => this.loadpage = params['loadpage']);

    // initialise
    this.advSearch = false;
    this.hideSaveSearch = false;
    this.vulnsLoaded = false;
    this.analyticsDataLoaded = false;
    this.loadStackedBarSeverity = false;
    this.updateStackedBarSeverity = false;
    this.currentPage = 1;

    // count the signals since last login
    this.userprefService.getUserLastLoginDate()
      .then(res => {
        this.vulnService.vulnsCountSinceLastLogin(res)
          .then(res => {
            this.sinceLastLogin = res;
          })
          .catch(err => {
            throw err;
          });
      })
      .catch(err => {
        this.handlerError(err);
      })

    // load the flags vulns from the user preferences
    this.refreshVulnUserPrefs();
    // load default page for navigation
    if(!this.loadpage || this.loadpage === 'latest'){
      this.page = "vulns";
      this.latestVulns();
    }else if(this.loadpage === 'lastlogin'){
      this.loadSinceLastVisit();
    }else if(this.loadpage === '7days'){
      this.loadSinceXDays(7);
    }
  }

  sortCVEs(field: string){
    if(this.sort === field && this.sortOrder){
      if(this.sortOrder == -1) {
        this.sortOrder = 1;
      }else{
        this.sortOrder = -1;
      }
    }else{
      this.sort = field;
      this.sortOrder = -1;
    }

    if(this.searched){
      this.search();
    }else{
      if(!this.loadpage || this.loadpage === 'latest'){
        this.page = "vulns";
        this.latestVulns();
      }else if(this.loadpage === 'lastlogin'){
        this.loadSinceLastVisit();
      }else if(this.loadpage === '7days'){
        this.loadSinceXDays(7);
      }
    }
  }

  /**
   * Refresh the local flagRefs, savedSearches and viewType variable from user preferences
   */
  refreshVulnUserPrefs(): Promise<void>{
    // load the flagged signals from user preferences
    return new Promise((resolve,reject) => {
      this.userprefService.getUserPreferences()
        .then(res => {
          if(res.flags && res.flags.vulns){
            this.flagRefs = res.flags.vulns;
          }else{
            this.flagRefs = [];
          }
          if(res.watched && res.watched.products){
            this.watchedProducts = res.watched.products;
          }else{
            this.watchedProducts = [];
          }
          if(res.vulnViewType){
            this.viewType = res.vulnViewType;
          }else{
            this.viewType = 'list';
          }
          if(res.searches && res.searches.vuln){
            this.savedSearches = res.searches.vuln;
          }else{
            this.savedSearches = [];
          }

          resolve();
        })
        .catch(err => reject(err));
    });
  }

  /**
   * Search on a bunch of attributes for a signal
   */
  search() { 
    this.currentPage = 1;
    this.page === 'vulns';
    this.searched = true;
    this.searchCriteria = new VulnSearchCriteria();
    this.searchCriteria.returnStub = true;
    this.searchCriteria.limit = this.perPage;

    if(this.cve){
      this.searchCriteria.cve = this.cve;
    }
    if(this.vendor){
      this.searchCriteria.vendor = this.vendor;
    }
    if(this.product){
      this.searchCriteria.product = this.product;
    }
    if(this.description){
      this.searchCriteria.description = this.description;
    }
    if(this.severity){
      this.searchCriteria.severity = this.severity;
    }
    if(this.cpe){
      this.searchCriteria.cpe = this.cpe;
    }
    if(this.dateFrom){
      this.searchCriteria.pubDateFrom = this.dateFrom;
    }
    if(this.dateTo){
      this.searchCriteria.pubDateTo = this.dateTo;
    }
    if(this.sort && this.sortOrder) {
      this.searchCriteria.sort = this.sort;
      this.searchCriteria.sortOrder = Number(this.sortOrder);
    }

    this.vulnSearch();
  }

  /**
   * Reset all the advanced fields
   */
  reset(){
    delete this.cve;
    delete this.vendor;
    delete this.product;
    delete this.description;
    delete this.severity;
    delete this.dateFrom;
    delete this.dateTo;
    delete this.cpe;

    delete this.sort;
    this.sortOrder = -1;

    this.searched = false;
  }

  /**
   * Add a cve reference to the user's preferences so they can flag signals
   * @param cveRef 
   */
  flag(cveRef: string) {
    // note which ref is being processed so we can stop user double clicking
    this.flaggingRef = cveRef;

    // update the users flags appropriately
    this.userprefService.userPreferencesUpdateFlags(constants.local.flags.vulns, cveRef, this.flagRefs.includes(cveRef))
      .then(res => {
        this.refreshVulnUserPrefs()
          .then(res => {
            this.flaggingRef = undefined;
          })
          .catch(err => { throw err });
      })
      .catch(err => {
        this.handlerError(err);
      })
  }

  /**
   * Add a product name to the user's preferences so they can watch products
   * @param product 
   * @param deleted whether to delete or create
   */
  watch(product: string, deleted: boolean) {
    // update the users flags appropriately
    this.userprefService.userPreferencesUpdateWatchedProducts(constants.local.watch.vuln, product.toLowerCase(), deleted)
      .then(res => {
        this.refreshVulnUserPrefs()
          .then(() => {
            this.loadWatched();
            this.page === 'watched'
          });
      })
      .catch(err => {
        this.handlerError(err);
      })
  }


  /**
   * Toggle between grid/list view
   * @param view 
   */
  changeViewType(view: string){
    // set local variable to determine what view the user is using
    this.viewType = view;

    // update the users flags appropriately
    this.userprefService.userPreferencesUpdateSingle(constants.local.views.vuln, view)
      .then(res => {
        this.refreshVulnUserPrefs()
          .catch(err => { throw err });
      })
      .catch(err => {
        this.handlerError(err);
      })

  }

  /**
   * Return the top 10 vulns
   */
  latestVulns(): void {
    this.searched = false;
    this.currentPage = 1;

    this.searchCriteria = new VulnSearchCriteria();
    this.searchCriteria.returnStub = true;
    this.searchCriteria.limit = this.perPage;
    this.searchCriteria.skip = this.perPage * (this.currentPage-1);

    if(this.sort && this.sortOrder) {
      this.searchCriteria.sort = this.sort;
      this.searchCriteria.sortOrder = Number(this.sortOrder);
    }

    this.vulnSearch();
  }

  /**
   * Pagination
   * @param event 
   */
  onPageChange(event): void {
    this.searchCriteria.skip = this.perPage * (this.currentPage-1);
    this.vulnSearch();
  }

  /** 
   * Utility method to load count of results as well as results themselves
   * Used for pagination
   */
  private vulnSearch(): Promise<void> {
    this.vulnsLoaded = false;
    return new Promise((resolve,reject) => {
      this.searchCriteria.count = true;
      let tempLimit = this.searchCriteria.limit;
      delete this.searchCriteria.limit;

      this.vulnService.vulnSearch(this.searchCriteria)
        .then(res => {
          if(res && res[0]){
            this.vulnsCount = res[0].matching_vulns;
          }else{
            this.vulnsCount = 0;
          }
          this.searchCriteria.count = false;
          this.searchCriteria.limit = tempLimit;
          this.vulnService.vulnSearch(this.searchCriteria)
            .then(res => {
              this.vulns = res;
              this.vulnsLoaded = true
              resolve();
            })
            .catch(err => {
              throw err;
            });
        })
        .catch(err => {
          reject();
          this.handlerError(err);
        });
    });
  }

  /** 
   *  Load flagged vulns 
   **/
  loadFlagged(): void {
    this.page = 'flagged';
    this.searched = false;

    if (this.flagRefs.length === 0) {
      this.vulns = [];
      this.vulnsCount = this.vulns.length;
    } else {
      this.vulnsLoaded = false;

      let loadListCriteria = new VulnSearchCriteria();
      loadListCriteria.limit = 12;
      loadListCriteria.returnStub = true;
      loadListCriteria.cveRefs = this.flagRefs;

      this.vulnService.vulnLoadList(loadListCriteria)
        .then(res => this.vulns = res)
        .then(() => {
          this.vulnsCount = this.vulns.length;
          this.vulnsLoaded = true;
        })
        .catch(err => {
          this.handlerError(err);
        })
    }
  }

   /** 
   *  Load watched products vulns 
   **/
  loadWatched(): void {
    this.page = 'watched';
    this.searched = false;

    if (this.watchedProducts.length === 0) {
      this.vulns = [];
      this.vulnsCount = this.vulns.length;
    } else {
      this.vulnsLoaded = false;

      let loadListCriteria = new VulnSearchCriteria();
      loadListCriteria.limit = 12;
      loadListCriteria.returnStub = true;
      loadListCriteria.products = this.watchedProducts;

      this.vulnService.vulnLoadProductList(loadListCriteria)
        .then(res => this.vulns = res)
        .then(() => {
          this.vulnsCount = this.vulns.length;
          this.vulnsLoaded = true;
        })
        .catch(err => {
          this.handlerError(err);
        })
    }
  }

  /**
   * Load all the annual analytics data for graphical presentation
   * Urgency and categories need formatting for charts
   */
  loadAnalytics(): void {
    this.page = 'analytics';
    this.analyticsDataLoaded = false;

    if(sessionStorage.getItem("vulnTotalProducts")){
      this.totalProducts = Number(sessionStorage.getItem("vulnTotalProducts"));
    }else{
      this.vulnService.vulnProductCount()
        .then(res => {
          this.totalProducts = res;
          sessionStorage.setItem("vulnTotalProducts", String(this.totalProducts));
        })
        .catch(err => {
          this.errorService.handlerError(err);
        });
    }
   
    if(sessionStorage.getItem("vulnTotalVendors")){
      this.totalVendors = Number(sessionStorage.getItem("vulnTotalVendors"));
    }else{
      this.vulnService.vulnVendorCount()
        .then(res => {
          this.totalVendors = res;
          sessionStorage.setItem("vulnTotalVendors", String(this.totalVendors));
        })
        .catch(err => {
          this.errorService.handlerError(err);
        });
    }
  
    if(sessionStorage.getItem("vulnTotalVulns")){
      this.totalVulnerabilities = Number(sessionStorage.getItem("vulnTotalVulns"));
    }else{
      this.vulnService.vulnCount()
        .then(res => {
          this.totalVulnerabilities = res;
          sessionStorage.setItem("vulnTotalVulns", String(this.totalVulnerabilities));
        })
        .catch(err => {
          this.errorService.handlerError(err);
        });
    }

    if(sessionStorage.getItem("vulnLastUpdated")){
      this.lastUpdated = sessionStorage.getItem("vulnLastUpdated");
    }else{
      this.vulnService.vulnLastUpdate()
        .then(res => {
          this.lastUpdated = res[0].lastModifiedDate;
          sessionStorage.setItem("vulnLastUpdated", this.lastUpdated);
        })
        .catch(err => {
          this.errorService.handlerError(err);
        });
    }

    // get last weeks 
    if(sessionStorage.getItem("vulnWeekWord")){
      this.wordChartOptions.series[0].data = JSON.parse(sessionStorage.getItem("vulnWeekWord"));
      this.updateWordChart = true;
    }else{
      let tempCrit = new VulnSearchCriteria();
      tempCrit.pubDateFrom = Utilities.getTodayMinusDays(7);
      tempCrit.pubDateTo = Utilities.getToday();
      tempCrit.returnStub = true;
  
      this.vulnService.vulnSearch(tempCrit)
        .then(res => {
          this.processVulnsWordCloud(res)
            .then(res => {
              let t = [];
              res.forEach(e => {
                if(e.weight > 1 && e.name.length > 2 && !constants.ignoreList.includes(e.name)){
                  t.push(e);
                }
              });
              t.sort((a,b) => b.weight - a.weight)
              sessionStorage.setItem("vulnWeekWord", JSON.stringify(t.slice(0,100)));
              this.wordChartOptions.series[0].data = t.slice(0,100);
              this.updateWordChart = true;
            })
        })
        .catch(err => {
          this.handlerError(err)
        });
    }
  
    // get 12 months stats 
    if(sessionStorage.getItem("vulnAnnualSeverityPet") && sessionStorage.getItem("vulnAnnualSeveritySeries") && sessionStorage.getItem("vulnAnnualSeverityCategories")){

      this.urgPieChartOptions.series[0].data = JSON.parse(sessionStorage.getItem("vulnAnnualSeverityPet"));
      this.stackedBarSeverityOptions.series = JSON.parse(sessionStorage.getItem("vulnAnnualSeveritySeries"));
      this.stackedBarSeverityOptions.xAxis.categories = JSON.parse(sessionStorage.getItem("vulnAnnualSeverityCategories"));

      this.updateUrgencyPie = true;
      this.updateStackedBarSeverity = true;
      this.loadStackedBarSeverity = true;
      this.analyticsDataLoaded = true;

    }else{
      
      let searchCriteria = new VulnSeverityStatsCriteria();
      searchCriteria.attributeName = constants.fields.vulns.severity;
      searchCriteria.pubDateFrom = Utilities.get12MonthsAgo()
      searchCriteria.pubDateTo = Utilities.getToday();

      this.vulnService.vulnMonthlyStats(searchCriteria)
        .then(res => {
          this.stackedBarSeverityOptions.xAxis.categories = [];

          // initialise an array for each urgency
          let none = { name: "None", data: []};
          let low = { name: "Low", data: []};
          let med = { name: "Medium", data: []};
          let high = { name: "High", data: []};
          let crit = { name: "Critical", data: []};

          let totallow = 0;
          let totalmed = 0;
          let totalhigh = 0;
          let totalcrit = 0;
          
          let arrayIndex = 1;
          res.forEach(dataPoint => {
            // add this months data to categories
            let x = dataPoint.name.substring(4) + "/" + dataPoint.name.substring(0,4);
            this.stackedBarSeverityOptions.xAxis.categories.push(x);

            // get this months data point for each urgency
            dataPoint.data.forEach(dp => {
              if(dp._id.baseSeverity === "NONE"){
                none.data.push(dp.count);
              }else if(dp._id.baseSeverity === "LOW"){
                totallow += dp.count;
                low.data.push(dp.count);
              }else if(dp._id.baseSeverity === "MEDIUM"){
                totalmed += dp.count;
                med.data.push(dp.count);
              }else if(dp._id.baseSeverity === "HIGH"){
                totalhigh += dp.count;
                high.data.push(dp.count);
              }else if(dp._id.baseSeverity === "CRITICAL"){
                totalcrit += dp.count;
                crit.data.push(dp.count)
              }
            });

            // if there were no datapoints for this month then add a 0
            if(none.data.length != arrayIndex){
              none.data.push(0);
            }
            if(low.data.length != arrayIndex){
              low.data.push(0);
            }
            if(med.data.length != arrayIndex){
              med.data.push(0);
            }
            if(high.data.length != arrayIndex){
              high.data.push(0);
            }
            if(crit.data.length != arrayIndex){
              crit.data.push(0);
            }
            arrayIndex++;
          });

          let sevSeries = [];
          sevSeries.push(none);
          sevSeries.push(low);
          sevSeries.push(med);
          sevSeries.push(high);
          sevSeries.push(crit);

          this.urgPieChartOptions.series[0].data = [];
          this.urgPieChartOptions.series[0].data.push(["Low", totallow]);
          this.urgPieChartOptions.series[0].data.push(["Medium", totalmed]);
          this.urgPieChartOptions.series[0].data.push(["High", totalhigh]);
          this.urgPieChartOptions.series[0].data.push(["Critical", totalcrit]);

          sessionStorage.setItem("vulnAnnualSeverityPet", JSON.stringify(this.urgPieChartOptions.series[0].data));
          sessionStorage.setItem("vulnAnnualSeveritySeries", JSON.stringify(sevSeries));
          sessionStorage.setItem("vulnAnnualSeverityCategories", JSON.stringify(this.stackedBarSeverityOptions.xAxis.categories));

          this.updateUrgencyPie = true;
          this.stackedBarSeverityOptions.series = sevSeries;
          this.updateStackedBarSeverity = true;
          this.loadStackedBarSeverity = true;
          this.analyticsDataLoaded = true;
        });
      }

  }

    /**
   * Process the summry of each signal into a counted word list
   * for the use in a wordcloud
   */
  private processVulnsWordCloud(cves: CVE[]): Promise<WordCloudItem[]> {
    return new Promise((resolve,reject) => {
      // concat all the summary
      let totalWords = "";
      cves.forEach(s => {
        totalWords = totalWords + ' ' + s.cve.description.description_data[0].value.toLowerCase();
      });

      // break down into a word count
      let lines = totalWords.split(/[,\. \(\)]+/g);
      resolve(Highcharts.reduce(lines, function (arr, word) {
            let obj = Highcharts.find(arr, function (obj) {
                return obj.name === word;
            });
            if (obj) {
                obj['weight'] += 1;
            } else {
                obj = {
                    name: word,
                    weight: 1
                };
                arr.push(obj);
            }
            return arr;
        }, [])
      )
    });
  }

  /**
   * Load vulnerabilites since last login
   */
  loadSinceLastVisit() : void {
    this.page = 'vulns';
    this.vulnsLoaded = false;

    // since last login
    this.userprefService.getUserLastLoginDate()
      .then(res => {
        this.searchCriteria = new VulnSearchCriteria();
        this.searchCriteria.modDateFrom =  Utilities.getDateAsString(res);
        this.searchCriteria.modDateTo = Utilities.getTodayPlusDays(1);
        this.searchCriteria.limit = 12;
        this.searchCriteria.returnStub = true;

        if(this.sort && this.sortOrder) {
          this.searchCriteria.sort = this.sort;
          this.searchCriteria.sortOrder = Number(this.sortOrder);
        }

        this.vulnSearch()
          .then(() => this.vulnsLoaded = true)
          .catch(err => {
            throw err;
          })
      })
      .catch(err => {
        this.handlerError(err);
      })
  }

  /**
   * Load vulnerabilites since x days ago
   */
  loadSinceXDays(days: number) : void {
    this.page = 'vulns';
    this.vulnsLoaded = false;

    // since last login
    this.userprefService.getUserLastLoginDate()
      .then(res => {
        this.searchCriteria = new VulnSearchCriteria();
        this.searchCriteria.pubDateFrom = Utilities.getTodayMinusDays(days);
        this.searchCriteria.pubDateTo = Utilities.getToday();
        this.searchCriteria.limit = 12;
        this.searchCriteria.returnStub = true;
        
        if(this.sort && this.sortOrder) {
          this.searchCriteria.sort = this.sort;
          this.searchCriteria.sortOrder = Number(this.sortOrder);
        }

        this.vulnSearch()
          .then(() => this.vulnsLoaded = true)
          .catch(err => {
            throw err;
          })
      })
      .catch(err => {
        this.handlerError(err);
      })
  }

  /**
   * Open modal
   * @param content 
   */
  openSearchModal(content) {
    this.modalService.open(content, { ariaLabelledBy: 'search-modal' }).result
      .then((result) => {
        if(result == 'save'){
          this.saveSearch();
        }
      }, (reason) => {
      });
  }

  /**
   * Save the search criteria in user preferences for future use
   */
  saveSearch() : void {
    let copy = (JSON.parse(JSON.stringify(this.searchCriteria)));
    delete copy.count;
    delete copy.limit;
    delete copy.skip;
    delete copy.sort;
    delete copy.sortOrder;
    copy.name = this.searchName;

    this.userprefService.userPreferencesUpdateChildArray("searches", "vuln", copy, false)
      .then(res => {
        this.searchName = undefined;
        this.refreshVulnUserPrefs();
        this.hideSaveSearch = true;
      });

  }

  /**
   * Load a saved search and run
   * @param savedSearch 
   */
  loadSearch(savedSearch: VulnSearchCriteria){
    this.searchCriteria = savedSearch;
    this.vulnSearch();
  }

  /**
   * Delete a saved search
   * @param event 
   * @param content 
   * @param savedSearch 
   */
  deleteSearch(event, content, savedSearch: VulnSearchCriteria): void {
    event.stopPropagation();
    this.modalService.open(content, { ariaLabelledBy: 'search-modal' }).result
      .then((result) => {
        if(result == 'delete'){
          this.userprefService.userPreferencesUpdateChildArray("searches", "vuln", savedSearch, true)
            .then(res => {
              this.refreshVulnUserPrefs();
            });
        }
      }, (reason) => {
      });
  }

  /**
   * Typeahead function for vendor
   * @param text$ 
   */
  vendorTA = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => term.length < 2 ? [] :
        this.vulnService.vulnVendorName(term.toLowerCase()).pipe(
          catchError(() => {
            return of([]);
          }))
      )
    )

  /**
   * Typeahead function for product/vendor
   * Vendor ignored at backend if null or empty
   * @param text$ 
   */
  productTA = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => term.length < 2 ? [] :
        this.vulnService.vulnProductName(term.toLowerCase(), this.vendor).pipe(
          catchError(() => {
            return of([]);
          }))
      )
    )

  /**
   * Error handling forwarder, only not going direct so 
   * it allows us to add context within this module
   * @param err 
   */
  private handlerError(err){
    this.errorService.handlerError(err);
  }
}
